import { STAGE_STATUS } from '../../../constants/constants';
import { useState, useEffect, useRef } from 'react';
import { addTimer, deleteTimer, getTimer, resumeTimer, pauseTimer } from '../../../utilis/helpers';

const INTRO_COUNTDOWN_TIMER_KEY = 'introCountdown';

export const useIntroCountdown = (
  canStart = false,
  initialCountdown,
  isIntroSStage,
  isPaused = false,
  time = 0,
  introToken
) => {
  const [showIntro, setShowIntro] = useState(true);
  // Mirrors `time` (the page's real elapsed clock, state.time) into a ref so
  // the pause/resume effect below can read the latest value without
  // depending on it — `time` changes every ~30ms and adding it as a
  // dependency would re-fire that effect on every tick.
  const timeRef = useRef(time);

  useEffect(() => {
    timeRef.current = time;
  }, [time]);

  // isIntroSStage is the raw backend stage value (STAGE_STATUS.*) — re-show the
  // intro every time the backend re-enters INTRO (e.g. moving on to the next
  // exercise), and hide it for every other stage. `canStart` alone won't
  // re-trigger this since it can stay true continuously across exercises.
  // Before the first websocket message arrives isIntroSStage is undefined —
  // default to showing the intro in that case too, matching the initial state.
  useEffect(() => {
    if (isIntroSStage == null || isIntroSStage === STAGE_STATUS.INTRO) {
      setShowIntro(true);
    } else {
      setShowIntro(false);
    }
  }, [isIntroSStage]);

  // The backend hasn't sent introInterval yet — nothing to seed the timer
  // with, so surface a loading state instead of counting down from a
  // made-up default.
  const isLoading = typeof initialCountdown !== 'number' || initialCountdown <= 0;

  // Depend on initialCountdown directly (not a ref) — the backend-provided
  // interval can arrive after canStart/showIntro already flipped true, and
  // without this dependency the timer below never picks up the real value.
  // isPaused is intentionally NOT a dependency here — toggling it must not
  // reseed the countdown, only the separate pause/resume effect below reacts
  // to it, so pausing/resuming preserves whatever value was left on the clock.
  //
  // introToken also has to be a dependency: skipping mid-INTRO moves straight
  // to the next exercise's own INTRO, which is the same STAGE_STATUS.INTRO
  // value (showIntro never toggles) and often the same interval length, so
  // neither dependency above changes — without introToken the ring kept
  // whatever count was left on the skipped exercise instead of restarting.
  useEffect(() => {
    if (!canStart || !showIntro || isLoading) {
      deleteTimer({ key: INTRO_COUNTDOWN_TIMER_KEY });
      return;
    }

    addTimer({ key: INTRO_COUNTDOWN_TIMER_KEY, value: timeRef.current });
  }, [canStart, showIntro, isLoading, initialCountdown, introToken]);

  // Kept separate from the seeding effect above so opening/closing the
  // settings modal (isPaused) only pauses/resumes the shared timer entry —
  // it never reseeds it, so closing the modal resumes the count from
  // wherever it was left instead of restarting it.
  useEffect(() => {
    if (!canStart || !showIntro || isLoading) {
      return;
    }

    if (isPaused) {
      pauseTimer({ key: INTRO_COUNTDOWN_TIMER_KEY, time: timeRef.current });
    } else {
      // No-op if the timer isn't actually paused (e.g. right after the
      // seeding effect above just anchored it fresh in this same commit).
      resumeTimer({ key: INTRO_COUNTDOWN_TIMER_KEY, time: timeRef.current });
    }
  }, [canStart, showIntro, isLoading, isPaused]);

  // No local ticking state — `time` is already re-driven every ~30ms by the
  // page's own timer, so the remaining seconds are derived straight from
  // getTimer on every render, the same drift-resistant mechanism every other
  // timer in the app (userTimer, eft-time, ...) already relies on. totalValue
  // is computed straight from `initialCountdown` (not the ref) because the
  // seeding effect below hasn't committed yet on the very first render where
  // canStart/showIntro/isLoading flip true — reading the ref here would still
  // see its stale 0 and getTimer's Decrease strategy throws on a falsy total.
  const totalValueMs = isLoading ? 0 : initialCountdown * 1000;
  const remainingMs =
    canStart && showIntro && !isLoading
      ? getTimer({
          key: INTRO_COUNTDOWN_TIMER_KEY,
          timer: time,
          totalValue: totalValueMs,
          strategy: 'Decrease',
        })
      : 0;
  const countdown =
    showIntro && !isLoading && !canStart
      ? initialCountdown
      : Math.max(0, Math.ceil(remainingMs / 1000));

  return { showIntro, countdown, isLoading };
};
